import React, { createContext, useContext, useEffect, useState } from 'react';
import { addToCart, checkoutCart, getCart, removeFromCart } from "../http/paymentsAPI";
import { promocodeApply } from "../http/promocodeAPI";
import { useNotifications } from "./NotificationContext";

const CartContext = createContext(null);

export const useCart = () => {
    return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [loading, setLoading] = useState(false);
    const { showNotification } = useNotifications();

    /**
     * Загружаем корзину с сервера при каждом рендере.
     */
    useEffect(() => {
        if (items.length === 0) {  // Only fetch if cart is empty
            fetchCart();
        }
    }, [items]);  // Depend on items, so it runs when items changes


    /**
     * Получает корзину с сервера и обновляет состояние.
     */
    const fetchCart = async () => {
        setLoading(true);
        try {
            const response = await getCart();

            // Устанавливаем товары в корзине
            setItems(response?.items || []);

            setTotalPrice(response.total_price);

        } catch (err) {
            showNotification('Не удалось загрузить корзину', "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };


    /**
     * Добавляет товар в корзину и обновляет данные с бэка.
     */
    const addItem = async (item) => {
        setLoading(true);
        try {
            const response = await addToCart({
                course_id: item.course_id,
                months: item.months,
                option_ids: item.option_ids,
            });
            await fetchCart(); // Загружаем актуальные данные
            showNotification("Курс добавлен в корзину", "success");
        } catch (err) {
            showNotification('Ошибка при добавлении в корзину', "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Удаляет товар из корзины и обновляет данные с бэка.
     */
    const removeItem = async (itemId) => {
        setLoading(true);
        try {
            await removeFromCart({ item_id: itemId });
            await fetchCart(); // Загружаем актуальные данные
        } catch (err) {
            showNotification('Ошибка при удалении из корзины', "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Оформляет заказ, очищает корзину и обновляет данные.
     */
    const checkout = async () => {
        setLoading(true);
        try {
            await checkoutCart(items);
            await fetchCart(); // Загружаем пустую корзину
            showNotification("Оплата прошла успешно", "success");
        } catch (err) {
            showNotification('Ошибка при оплате', "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Применяет промокод и обновляет цену корзины.
     */
    const applyPromoCode = async (promoCode) => {
        if (!promoCode) return;
        try {
            const { body } = await promocodeApply(promoCode);
            setTotalPrice(body.new_total_price);
            showNotification("Промокод успешно применён", "success");
        } catch (error) {
            console.error('Ошибка:', error);
            showNotification('Ошибка при применении промокода', "error");
        }
    };

    return (
        <CartContext.Provider value={{ items, addItem, removeItem, checkout, fetchCart, applyPromoCode, totalPrice, loading }}>
            {children}
        </CartContext.Provider>
    );
};
